import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { Row, Col, Card, Button, Form, Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { store } from '../../../../store/store';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import MyApiClient from '../../../../api-client/MyApiClient';
import moment from 'moment';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ColumnFilter } from '../../../components/table/FilteringTable/ColumnFilter';

const ManagerSubscriptionsList = () => {
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        dateFrom: '',
        dateTo: ''
    });
    // Vista fatturazione: 'with' (default) mostra solo gli abbonamenti con fattura,
    // 'without' quelli senza, 'all' tutti.
    const [invoiceView, setInvoiceView] = useState('with');
    // Ricerca testuale client-side (club, email, telefono, n. documento).
    const [search, setSearch] = useState('');

    // --- Modale abbonamento gratuito ---
    const [showFreeModal, setShowFreeModal] = useState(false);
    const [freeData, setFreeData] = useState({ startDate: '', endDate: '' });
    const [freeClubSelected, setFreeClubSelected] = useState(null);
    const [savingFree, setSavingFree] = useState(false);
    const [freeError, setFreeError] = useState('');

    const loadClubOptions = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new MyApiClient();
            const res = await apiClient.getProfilesByFilters(token, 20, inputValue, 'CLUB', 0, 'NAME', true);
            if (res && res.items) {
                return res.items.map(club => ({
                    value: club.userId,
                    label: club.email ? `${club.name} — ${club.email}` : club.name,
                    club,
                }));
            }
        } catch (e) {
            console.error(e);
        }
        return [];
    };

    const handleOpenFreeModal = () => {
        setFreeData({ startDate: '', endDate: '' });
        setFreeClubSelected(null);
        setFreeError('');
        setShowFreeModal(true);
    };

    const handleSaveFree = async () => {
        if (!freeClubSelected) { setFreeError('Seleziona una struttura.'); return; }
        if (!freeData.startDate) { setFreeError('Inserisci la data di inizio.'); return; }
        if (!freeData.endDate) { setFreeError('Inserisci la data di fine.'); return; }
        if (freeData.endDate <= freeData.startDate) { setFreeError('La data di fine deve essere successiva alla data di inizio.'); return; }
        setFreeError('');
        setSavingFree(true);
        try {
            const token = store.getState().auth.auth.idToken;
            const client = new ManagerSubscriptionsApiClient();
            const res = await client.createFreeAdminSubscription(token, {
                clubId: freeClubSelected.value,
                startDate: freeData.startDate,
                endDate: freeData.endDate
            });
            if (res === true) {
                setShowFreeModal(false);
                fetchSubscriptions();
            } else {
                setFreeError(res.error || 'Errore durante il salvataggio');
            }
        } catch (e) {
            console.error(e);
            setFreeError('Errore di connessione');
        } finally {
            setSavingFree(false);
        }
    };
    // --- Fine modale abbonamento gratuito ---

    const dotStat = (color, label, muted = false) => (
        <span className={`sid-dotstat${muted ? ' muted' : ''}`}>
            <span className="d" style={{ background: color }}></span>{label}
        </span>
    );

    const STATUS_META = {
        active:   { color: '#22C55E', label: 'Attivo' },
        expired:  { color: '#EF4444', label: 'Scaduto' },
        free:     { color: '#0EA5E9', label: 'Gratuito' },
        inactive: { color: '#9AA1AB', label: 'Inattivo' },
        refunded: { color: '#F59E0B', label: 'Rimborsato' },
    };

    const getStatusBadge = (status) => {
        const m = STATUS_META[status] || { color: '#9AA1AB', label: status };
        return dotStat(m.color, m.label);
    };

    // Un abbonamento è "con fattura" quando è stata generata una fattura/transazione.
    const hasInvoice = (sub) => !!(sub.invoiceStatus || sub.transactionId);

    const getPaymentMethodBadge = (sub) => {
        let method = sub.paymentMethod;
        if (!method) {
            // Fallback per record senza paymentMethod (es. prima dell'esposizione del campo lato backend).
            if (sub.status === 'free' || !sub.priceEur) return <span className="text-muted">Gratuito</span>;
            if (sub.stripeSubscriptionId) method = 'card';
        }
        switch (method) {
            case 'card': return <><i className="fa fa-credit-card me-1 text-primary"></i> Carta</>;
            case 'paypal': return <><i className="fa fa-paypal me-1 text-info"></i> PayPal</>;
            case 'bank_transfer': return <><i className="fa fa-bank me-1 text-secondary"></i> Bonifico</>;
            default: return <span className="text-muted">-</span>;
        }
    };

    const INVOICE_META = {
        SENT:      { color: '#3B82F6', label: 'Inviata' },
        ACCEPTED:  { color: '#22C55E', label: 'Accettata' },
        DELIVERED: { color: '#22C55E', label: 'Consegnata' },
        REJECTED:  { color: '#EF4444', label: 'Scartata SDI' },
        NOT_SENT:  { color: '#F97316', label: 'Da inviare' },
    };

    const getInvoiceBadge = (status) => {
        const m = INVOICE_META[status] || { color: '#9AA1AB', label: status };
        return dotStat(m.color, m.label);
    };

    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const token = store.getState().auth.auth.idToken;
            const client = new ManagerSubscriptionsApiClient();

            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo) params.dateTo = filters.dateTo;

            const data = await client.getAdminSubscriptions(token, params);
            if (!data.error) {
                setSubscriptions(data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const applyFilters = () => {
        fetchSubscriptions();
    };

    // La riga apre la scheda dell'abbonamento (pagina, non più finestra): modifica e azioni sulla fattura stanno lì (03/10/2026)
    const apriScheda = (sub) => navigate('/superadmin/manager-subscriptions/scheda/' + sub.id, { state: { abbonamento: sub } });

    // "Da gestire" = le fatture nello stesso stato conteggiato dal badge laterale del menu:
    // NOT_SENT (ancora da inviare allo SDI) o REJECTED (scartate dallo SDI, da correggere).
    const isToHandle = (sub) => sub.invoiceStatus === 'NOT_SENT' || sub.invoiceStatus === 'REJECTED';

    const withInvoiceCount = useMemo(() => subscriptions.filter(hasInvoice).length, [subscriptions]);
    const toHandleCount = useMemo(() => subscriptions.filter(isToHandle).length, [subscriptions]);
    const notSentCount = useMemo(() => subscriptions.filter((s) => s.invoiceStatus === 'NOT_SENT').length, [subscriptions]);
    const rejectedCount = useMemo(() => subscriptions.filter((s) => s.invoiceStatus === 'REJECTED').length, [subscriptions]);

    const filteredSubscriptions = useMemo(() => {
        let list = subscriptions;
        if (invoiceView === 'toHandle') list = list.filter(isToHandle);
        else if (invoiceView === 'with') list = list.filter(hasInvoice);
        else if (invoiceView === 'without') list = list.filter((s) => !hasInvoice(s));

        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter((s) =>
                [s.clubName, s.clubEmail, s.clubPhone, s.transactionId]
                    .some((v) => (v || '').toString().toLowerCase().includes(q))
            );
        }
        return list;
    }, [subscriptions, invoiceView, search]);

    const data_column = useMemo(() => [
        {
            Header: 'Club',
            accessor: 'clubName',
            Filter: ColumnFilter,
            Cell: ({ value }) => <span style={{ fontWeight: 600 }}>{value}</span>
        },
        {
            Header: 'Email',
            accessor: 'clubEmail',
            Filter: ColumnFilter,
        },
        {
            Header: 'Telefono',
            accessor: 'clubPhone',
            Filter: ColumnFilter,
            Cell: ({ value }) => value || '-'
        },
        {
            Header: 'Num. Doc.',
            accessor: 'transactionId',
            Filter: ColumnFilter,
            Cell: ({ value }) => value || '-'
        },
        {
            Header: 'Data Inizio',
            accessor: 'startDate',
            Filter: ColumnFilter,
            Cell: ({ value }) => value ? moment(value).format('DD/MM/YYYY') : '-'
        },
        {
            Header: 'Data Fine',
            accessor: 'endDate',
            Filter: ColumnFilter,
            Cell: ({ value }) => value ? moment(value).format('DD/MM/YYYY') : '-'
        },
        {
            Header: 'Importo',
            accessor: 'priceEur',
            disableFilters: true,
            Cell: ({ value }) => value ? `€ ${Number(value).toFixed(2)}` : '-'
        },
        {
            Header: 'Pagamento',
            accessor: 'paymentMethod',
            disableFilters: true,
            Cell: ({ row }) => getPaymentMethodBadge(row.original)
        },
        {
            Header: 'Rinnovo',
            accessor: 'stripeSubscriptionId',
            disableFilters: true,
            Cell: ({ row }) => {
                const sub = row.original;
                if (sub.stripeSubscriptionId) {
                    return sub.cancelAtPeriodEnd
                        ? <span className="sid-rin annullato">Annullato</span>
                        : <span className="sid-rin auto">Automatico</span>;
                }
                return <span className="sid-rin manuale">Manuale</span>;
            }
        },
        {
            Header: 'Stato Fatt.',
            accessor: 'invoiceStatus',
            disableFilters: true,
            Cell: ({ row }) => {
                const sub = row.original;
                const errors = sub.sdiRejectionErrors || sub.sdi_rejection_errors;
                return (
                    <div className="d-flex align-items-center">
                        {sub.invoiceStatus ? getInvoiceBadge(sub.invoiceStatus) : dotStat('#9AA1AB', 'Senza fattura', true)}
                        {sub.invoiceStatus === 'REJECTED' && errors?.length > 0 && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>
                                        {errors.map((err, i) => (
                                            <div key={i}><strong>{err.Codice}</strong>: {err.Descrizione}</div>
                                        ))}
                                    </Tooltip>
                                }
                            >
                                <i className="fa fa-exclamation-triangle text-danger ms-2" style={{ cursor: 'pointer' }}></i>
                            </OverlayTrigger>
                        )}
                    </div>
                );
            }
        },
        {
            Header: 'Stato Abb.',
            accessor: 'status',
            Filter: ColumnFilter,
            Cell: ({ value }) => getStatusBadge(value)
        },
    ], []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="sid-managersubs">
            {toHandleCount > 0 && (
                <div className="sid-fatt-alert">
                    <i className="fa fa-exclamation-triangle"></i>
                    <div className="sid-fatt-alert__txt">
                        <strong>{toHandleCount} {toHandleCount === 1 ? 'fattura da gestire' : 'fatture da gestire'}</strong>
                        {' — '}
                        {notSentCount > 0 && <>{notSentCount} da inviare allo SDI</>}
                        {notSentCount > 0 && rejectedCount > 0 && ', '}
                        {rejectedCount > 0 && <>{rejectedCount} scartate dallo SDI da correggere</>}.
                        <span className="sid-fatt-alert__hint">
                            È lo stesso numero indicato dal badge accanto a «Elenco Abbonamenti» nel menu laterale.
                        </span>
                    </div>
                    {invoiceView !== 'toHandle' && (
                        <button type="button" className="sid-fatt-alert__btn" onClick={() => setInvoiceView('toHandle')}>
                            Gestisci
                        </button>
                    )}
                </div>
            )}
            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Body>
                            <div className="sid-fatt-chips">
                                <button
                                    type="button"
                                    className={`sid-fatt-chip${invoiceView === 'with' ? ' active' : ''}`}
                                    onClick={() => setInvoiceView('with')}
                                >
                                    Con fattura <span className="n">{withInvoiceCount}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`sid-fatt-chip warn${invoiceView === 'toHandle' ? ' active' : ''}`}
                                    onClick={() => setInvoiceView('toHandle')}
                                    title="Fatture da inviare allo SDI o scartate da correggere"
                                >
                                    <i className="fa fa-exclamation-triangle"></i> Da gestire <span className="n">{toHandleCount}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`sid-fatt-chip${invoiceView === 'without' ? ' active' : ''}`}
                                    onClick={() => setInvoiceView('without')}
                                >
                                    Senza fattura <span className="n">{subscriptions.length - withInvoiceCount}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`sid-fatt-chip${invoiceView === 'all' ? ' active' : ''}`}
                                    onClick={() => setInvoiceView('all')}
                                >
                                    Tutti <span className="n">{subscriptions.length}</span>
                                </button>
                            </div>
                            <Form className="d-flex align-items-end gap-3 flex-wrap">
                                <Form.Group className="flex-grow-1" style={{ minWidth: 240 }}>
                                    <Form.Label>Cerca</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Club, email, n. documento…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Stato</Form.Label>
                                    <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                                        <option value="">Tutti</option>
                                        <option value="active">Attivo</option>
                                        <option value="free">Gratuito</option>
                                        <option value="expired">Scaduto</option>
                                        <option value="inactive">Inattivo</option>
                                        <option value="refunded">Rimborsato</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Data Inizio Da</Form.Label>
                                    <Form.Control type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Data Inizio A</Form.Label>
                                    <Form.Control type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
                                </Form.Group>
                                <Button variant="primary" size="sm" className="sid-btn-square" onClick={applyFilters} disabled={loading}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Filtra'}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <TabellaLista
                cardTitle="Abbonamenti Manager (Club)"
                pageList={true}
                hasSearch={false}
                hasFilter={false}
                data_column={data_column}
                openColumn={'clubName'}
                openRow={(row) => apriScheda(row)}
                data_json={filteredSubscriptions}
                sort_by="startDate"
                sort_type="desc"
                headerButtons={
                    <Button variant="primary" size="sm" className="sid-btn-accent" onClick={handleOpenFreeModal}>
                        <i className="fa fa-plus me-1"></i> Inserisci abbonamento gratuito
                    </Button>
                }
            />

            {/* Modal abbonamento gratuito */}
            <Modal show={showFreeModal} onHide={() => setShowFreeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Inserisci abbonamento gratuito</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Struttura</Form.Label>
                            <AsyncSelect
                                loadOptions={loadClubOptions}
                                onChange={(opt) => setFreeClubSelected(opt)}
                                value={freeClubSelected}
                                placeholder="Cerca struttura per nome..."
                                loadingMessage={() => 'Ricerca...'}
                                noOptionsMessage={({ inputValue }) => inputValue.length < 2 ? 'Digita almeno 2 caratteri' : 'Nessun risultato'}
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data Inizio</Form.Label>
                            <Form.Control
                                type="date"
                                value={freeData.startDate}
                                onChange={(e) => setFreeData({ ...freeData, startDate: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data Fine</Form.Label>
                            <Form.Control
                                type="date"
                                value={freeData.endDate}
                                onChange={(e) => setFreeData({ ...freeData, endDate: e.target.value })}
                            />
                        </Form.Group>
                        {freeError && (
                            <div className="alert alert-danger py-2 mb-0" role="alert">
                                <i className="fa fa-exclamation-circle me-2"></i>{freeError}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFreeModal(false)} disabled={savingFree}>Annulla</Button>
                    <Button variant="success" onClick={handleSaveFree} disabled={savingFree}>
                        {savingFree ? <><Spinner animation="border" size="sm" className="me-1" /> Salvataggio...</> : 'Inserisci'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default ManagerSubscriptionsList;
